import { generateSecretToken } from "@fleet/utils";

const ALIAS_STORAGE_KEY = "bodega_account_aliases";

type AliasMap = Record<string, string>;

function readAliasMap(): AliasMap {
  try {
    const raw = globalThis.localStorage.getItem(ALIAS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const map: AliasMap = {};
    for (const [alias, userId] of Object.entries(parsed)) {
      if (
        typeof alias === "string" &&
        typeof userId === "string" &&
        alias !== "" &&
        userId !== ""
      ) {
        map[alias] = userId;
      }
    }
    return map;
  } catch {
    return {};
  }
}

function writeAliasMap(map: AliasMap): void {
  try {
    globalThis.localStorage.setItem(ALIAS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage unavailable
  }
}

/**
 * Mints a random, non-guessable alias. Every supported runtime has `crypto`;
 * if it is somehow absent, throwing is better than a guessable alias.
 */
function mintAlias(): string {
  return generateSecretToken().replaceAll("-", "");
}

export function aliasForAccount(userId: string): string {
  const map = readAliasMap();
  for (const [alias, id] of Object.entries(map)) {
    if (id === userId) {
      return alias;
    }
  }
  const alias = mintAlias();
  map[alias] = userId;
  writeAliasMap(map);
  return alias;
}

export function accountIdForAlias(alias: string): string | null {
  return readAliasMap()[alias] ?? null;
}

export function pruneAliases(knownUserIds: readonly string[]): void {
  const map = readAliasMap();
  const known = new Set(knownUserIds);
  let changed = false;
  for (const [alias, userId] of Object.entries(map)) {
    if (!known.has(userId)) {
      delete map[alias];
      changed = true;
    }
  }
  if (changed) {
    writeAliasMap(map);
  }
}
